/**
 * The REST contract: one schema per request body and query string, and the
 * envelopes the responses come back in.
 *
 * Every mutating route parses its body with the schema from this file, and the
 * SPA builds its requests from the same one. That is the whole point — a field
 * renamed here breaks both sides at `pnpm typecheck` instead of at runtime, on
 * a Friday, in front of a candidate.
 */

import { z } from 'zod';
import {
  boardRowSchema,
  conversationDetailSchema,
  conversationSummarySchema,
  cursorSchema,
  demandSummarySchema,
  handlingStateSchema,
  jsonValueSchema,
  messageSchema,
  parkedRowSchema,
  parkReasonSchema,
  settingSchema,
  temperatureSchema,
} from './types.js';

/* -------------------------------------------------------------------------- */
/* Envelopes                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Keyset, not offset. Rows arrive constantly, and OFFSET 40 against a list that
 * gained three rows since the last page silently skips three conversations.
 * `nextCursor` is null when the caller has reached the end.
 */
export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

export const errorResponseSchema = z.object({
  error: z.string(),
  /** Field-level detail from a zod failure, keyed by dotted path. */
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

/** WhatsApp's own text limit. Longer bodies are rejected, never truncated. */
const MESSAGE_MAX = 4096;

/* -------------------------------------------------------------------------- */
/* GET /api/conversations                                                      */
/* -------------------------------------------------------------------------- */

export const conversationListQuerySchema = z.object({
  handlingState: handlingStateSchema.optional(),
  temperature: temperatureSchema.optional(),
  /** A user id, or `unassigned` for the rows nobody owns. */
  assignee: z.string().min(1).optional(),
  /** Matched against candidate name, push name, phone and last message. */
  search: z.string().min(1).max(120).optional(),
  cursor: cursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type ConversationListQuery = z.input<typeof conversationListQuerySchema>;

export const conversationListResponseSchema = pageOf(conversationSummarySchema);
export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>;

/* -------------------------------------------------------------------------- */
/* GET /api/conversations/:id                                                  */
/* -------------------------------------------------------------------------- */

export const conversationDetailQuerySchema = z.object({
  cursor: cursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Messages come back newest-first — that is the page a thread opens on. */
export const conversationDetailResponseSchema = z.object({
  conversation: conversationDetailSchema,
  messages: pageOf(messageSchema),
});
export type ConversationDetailResponse = z.infer<typeof conversationDetailResponseSchema>;

/* -------------------------------------------------------------------------- */
/* GET /api/board and /api/parked                                              */
/* -------------------------------------------------------------------------- */

export const boardQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(60),
});

export const boardResponseSchema = z.object({ rows: z.array(boardRowSchema) });
export type BoardResponse = z.infer<typeof boardResponseSchema>;

export const parkedResponseSchema = z.object({ rows: z.array(parkedRowSchema) });
export type ParkedResponse = z.infer<typeof parkedResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Conversation mutations                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The winner's 200. Mutations answer with the full detail rather than the
 * list-sized summary: the caller just changed this conversation and is about to
 * render it, so a second round trip for state it already caused is latency for
 * nothing.
 */
export const claimResponseSchema = z.object({ conversation: conversationDetailSchema });

/**
 * The loser's 409. It names who won so the toast can say it, rather than
 * leaving staff to guess why the row vanished.
 */
export const claimConflictSchema = z.object({
  error: z.literal('already_claimed'),
  claimedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
  handlingState: handlingStateSchema,
});
export type ClaimConflict = z.infer<typeof claimConflictSchema>;

export const takeoverBodySchema = z.object({
  /** Shown in the handoff trail. Why this conversation was pulled from the AI. */
  reason: parkReasonSchema.default('manual'),
});

export const handbackBodySchema = z.object({
  /**
   * An instruction for the AI, not a message to the candidate. Stored with the
   * candidate's facts because that object is what the model reads.
   */
  note: z.string().max(1000).optional(),
  /**
   * `resume` puts the flow back where the handover interrupted it; `restart`
   * takes it back to the beginning, for when the human changed the subject
   * entirely and resuming would answer a question nobody asked.
   */
  resume: z.enum(['resume', 'restart']).default('resume'),
});
export type HandbackBody = z.infer<typeof handbackBodySchema>;

export const agentMessageBodySchema = z.object({
  body: z.string().trim().min(1).max(MESSAGE_MAX),
});
export type AgentMessageBody = z.infer<typeof agentMessageBodySchema>;

export const noteBodySchema = z.object({
  body: z.string().trim().min(1).max(MESSAGE_MAX),
});
export type NoteBody = z.infer<typeof noteBodySchema>;

export const resolveBodySchema = z.object({
  note: z.string().max(1000).optional(),
});

export const messageCreatedResponseSchema = z.object({ message: messageSchema });
export const conversationResponseSchema = z.object({ conversation: conversationDetailSchema });

/* -------------------------------------------------------------------------- */
/* Settings and demands                                                        */
/* -------------------------------------------------------------------------- */

export const settingPutBodySchema = z.object({ value: jsonValueSchema });
export type SettingPutBody = z.infer<typeof settingPutBodySchema>;

export const settingResponseSchema = z.object({ setting: settingSchema });

export const demandListResponseSchema = z.object({ demands: z.array(demandSummarySchema) });
export type DemandListResponse = z.infer<typeof demandListResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Session                                                                     */
/* -------------------------------------------------------------------------- */

export const meResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.enum(['admin', 'agent']),
  }),
});
export type MeResponse = z.infer<typeof meResponseSchema>;
